import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import PropTypes from 'prop-types';

import apiRequest from '@/apiRequest';
import ClientFormComponent from '@/components/ClientsPage/ClientForm/ClientFormComponent';
import { useUser } from '@/contexts/UserContext';
import { parseInputName } from '@/helpers';
import { validateDocument } from '@/helpers/documentValidation';
import { masksByCountry } from '@/helpers/mask';
import AddressService from '@/services/addressService';
import apiCommercialRequest from '@/services/apiCommercialRequest';
import { showToast } from '@/store/ducks/toasts';

const classifications = [
  'IMOBILIARIA',
  'CORRETOR_DE_IMOVEIS',
  'PROPRIETARIO',
  'CONDOMINIO',
  'ADMINISTRADORA_DE_CONDOMINIOS',
  'SINDICO_PROFISSIONAL',
  'CONSTRUTORA_INCORPORADORA',
  'EMPREITEIRA',
  'INSTITUICAO_FINANCEIRA',
  'REDE_DE_VAREJO',
  'PESSOA_FISICA',
  'OUTROS',
];

const billingModalities = ['SEPARATE', 'PRE_PAID', 'POST_PAID'];

const ClientFormContainer = ({ action }) => {
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingCnpj, setLoadingCnpj] = useState(false);
  const [type, setType] = useState('pj');
  const [showBillingAddress, setShowBillingAddress] = useState(false);
  const [documentError, setDocumentError] = useState('');
  const [client, setClient] = useState({
    client_type: 'pj',
    contact: { assignment_email_rule: 'CREATOR' },
    address: {},
    billing_address: {},
  });
  const [billingModalitiesPanel, setBillingModalitiesPanel] = useState([
    'PAY_PER_USE',
    'BASIC_PLAN',
    'ESSENTIAL_PLAN',
    'ADVANCED_PLAN',
    'PROFESSIONAL_PLAN',
  ]);

  const [states, setStates] = useState([]);

  const [cities, setCities] = useState([]);
  const [billingCities, setBillingCities] = useState([]);

  const [neighborhoods, setNeighborhoods] = useState([]);
  const [billingNeighborhoods, setBillingNeighborhoods] = useState([]);

  const dispatch = useDispatch();

  const intl = useIntl();
  const navigate = useNavigate();
  const { id, status } = useParams();

  const me = useUser();
  const franchiseeCountry = me?.profile?.country || 'BR';
  const addressService = new AddressService(franchiseeCountry);

  const isBrazilianFranchisee = franchiseeCountry === 'BR';
  const useTextInputForAddress = ['UY', 'PY', 'MX', 'CO'].includes(franchiseeCountry);

  const zipcodeMask = masksByCountry[franchiseeCountry].ZIPCODE_MASK;
  const cnpjMask = masksByCountry[franchiseeCountry].CNPJ_MASK;
  const cpfOnlyMask = masksByCountry[franchiseeCountry].CPF_ONLY_MASK;
  const phoneMask = masksByCountry[franchiseeCountry].PHONE_MASK;
  const cellphoneMask = masksByCountry[franchiseeCountry].CELLPHONE_MASK;
  const cellphoneMaskLength = masksByCountry[franchiseeCountry].CELLPHONE_MASK_LENGTH;

  const handleNormalizeName = (name = '') => name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  // Document validation functions
  const validateDocumentField = (document, clientType) => {
    if (!document) {
      setDocumentError('');
      return true;
    }

    const isValid = validateDocument(document, clientType, franchiseeCountry);
    if (!isValid) {
      setDocumentError(intl.formatMessage({ id: 'CLIENT_LINK.INVALID_DOCUMENT' }));
    } else {
      setDocumentError('');
    }
    return isValid;
  };

  const fillNeighborhoods = async (parent, city) => {
    if (!city || city === '') {
      // Clear neighborhoods list but preserve existing values
      if (parent === 'address') setNeighborhoods([]);
      if (parent === 'billing_address') setBillingNeighborhoods([]);
      return [];
    }

    try {
      const data = await addressService.getNeighborhoodsByCity(city);

      // Only clear neighborhood selection if we successfully loaded new neighborhoods
      if (data && data.length > 0) {
        setClient(state => ({
          ...state,
          [parent]: {
            ...state[parent],
            neighborhood: null,
            neighborhood_id: null,
          },
        }));
      }

      if (parent === 'address') setNeighborhoods(data || []);
      if (parent === 'billing_address') setBillingNeighborhoods(data || []);

      return data || [];
    } catch (err) {
      console.debug('[DEBUG] fillNeighborhoods error ', err);
      return [];
    }
  };

  const fillCities = async (parent, state) => {
    // Don't make API call if state is empty, null, or undefined
    if (!state || state === '') {
      // Clear the cities and neighborhoods lists but preserve existing values
      if (parent === 'address') {
        setCities([]);
        setNeighborhoods([]);
      }
      if (parent === 'billing_address') {
        setBillingCities([]);
        setBillingNeighborhoods([]);
      }
      return [];
    }

    try {
      const data = await addressService.getCitiesByState(state);

      // Only clear city selection if we successfully loaded new cities
      if (data && data.length > 0) {
        setClient(oldState => ({
          ...oldState,
          [parent]: {
            ...oldState[parent],
            city: null,
            city_id: null,
          },
        }));
      }

      if (parent === 'address') setCities(data || []);
      if (parent === 'billing_address') setBillingCities(data || []);

      return data || [];
    } catch (err) {
      console.debug('[DEBUG] fillCities error ', err);
      return [];
    }
  };

  const fillAddress = async (parent, zipcode) => {
    try {
      const address = await addressService.getLocationByZipcode(zipcode);

      if (address) {
        // Only update if we got valid address data
        await fillCities(parent, address?.state_id);
        await fillNeighborhoods(parent, address?.city_id);

        setClient(prevClient => ({
          ...prevClient,
          [parent]: {
            ...prevClient[parent],
            street: address?.street || prevClient[parent]?.street || '',
            neighborhood: address?.neighborhood || prevClient[parent]?.neighborhood || '',
            neighborhood_id: address?.neighborhood_id || prevClient[parent]?.neighborhood_id || '',
            city: address?.city || prevClient[parent]?.city || '',
            city_id: address?.city_id || prevClient[parent]?.city_id || '',
            state: address?.state || prevClient[parent]?.state || '',
            state_id: address?.state_id || prevClient[parent]?.state_id || '',
            zipcode,
          },
        }));
      } else {
        // If no address found, just update the zipcode and preserve existing values
        setClient(prevClient => ({
          ...prevClient,
          [parent]: {
            ...prevClient[parent],
            zipcode,
          },
        }));
      }
    } catch (err) {
      console.debug('[DEBUG] fillAddress error ', err);
      // On error, just update the zipcode and preserve existing values
      setClient(prevClient => ({
        ...prevClient,
        [parent]: {
          ...prevClient[parent],
          zipcode,
        },
      }));
    }
  };

  const findCnpj = async documentValue => {
    if (loadingCnpj) return;
    setLoadingCnpj(true);

    try {
      const cnpjValue = documentValue.toUpperCase().replace(/[^A-Z0-9]+/g, '');
      const { data } = await apiCommercialRequest.get(`/companies/${cnpjValue}`);

      await fillAddress('address', data?.cep);

      setClient(state => ({
        ...state,
        company_name: data?.legal_name || '',
        name: data?.company_name || '',
        document: documentValue,
        address: {
          ...state['address'],
          number: data?.number || '',
          complement: data?.complement || '',
        },
        contact: {
          ...state['contact'],
          email: data?.email || '',
          phone: data?.first_phone || '',
        },
        hotsite_configuration: {
          ...state['hotsite_configuration'],
          cc_email: data?.email || '',
        },
      }));
    } catch (err) {
      console.debug('[DEBUG] findCnpj error ', err);

      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'SEARCH' }),
          text: intl.formatMessage({ id: 'cnpjNotFound' }),
          duration: 5000,
        })
      );
    } finally {
      setLoadingCnpj(false);
    }
  };

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const { data } = await apiRequest.get(`/clients/${id}`);

        if (data.address) {
          const stateId = data.address?.state_id || states.find(state => state?.name === data.address?.state)?.id;

          if (stateId) await fillCities('address', stateId);

          if (data.address?.city_id) {
            const fetchedNeighborhoods = await fillNeighborhoods('address', data.address.city_id);

            if (!data.address?.neighborhood_id && fetchedNeighborhoods?.length) {
              const neighborhoodFound = fetchedNeighborhoods.find(neighborhood => {
                return handleNormalizeName(neighborhood?.name) === handleNormalizeName(data.address?.neighborhood);
              });

              data.address.neighborhood_id = neighborhoodFound?.id || '';
            }
          }
        }

        if (data.billing_address) {
          const stateId =
            data.billing_address?.state_id || states.find(state => state?.name === data.billing_address?.state)?.id;

          if (stateId) await fillCities('billing_address', stateId);

          if (data.billing_address?.city_id) {
            const fetchedNeighborhoods = await fillNeighborhoods('billing_address', data.billing_address.city_id);

            if (!data.billing_address?.neighborhood_id && fetchedNeighborhoods?.length) {
              const neighborhoodFound = fetchedNeighborhoods.find(neighborhood => {
                return (
                  handleNormalizeName(neighborhood?.name) === handleNormalizeName(data.billing_address?.neighborhood)
                );
              });

              data.billing_address.neighborhood_id = neighborhoodFound?.id || '';
            }
          }
        }

        setType(data?.client_type);
        setClient(data);
        setShowBillingAddress(
          !!(
            data?.billing_address?.zipcode ||
            data?.billing_address?.street ||
            data?.billing_address?.number ||
            data?.billing_address?.complement ||
            data?.billing_address?.neighborhood ||
            data?.billing_address?.city ||
            data?.billing_address?.state
          )
        );

        if (data.modality === 'BASIC_LEGACY_PLAN') {
          setBillingModalitiesPanel([
            'PAY_PER_USE',
            'BASIC_LEGACY_PLAN',
            'BASIC_PLAN',
            'ESSENTIAL_PLAN',
            'ADVANCED_PLAN',
            'PROFESSIONAL_PLAN',
          ]);
        } else {
          setBillingModalitiesPanel([
            'PAY_PER_USE',
            'BASIC_PLAN',
            'ESSENTIAL_PLAN',
            'ADVANCED_PLAN',
            'PROFESSIONAL_PLAN',
          ]);
        }

        setLoading(false);
      } catch (err) {
        console.debug('[DEBUG] useEffect fetchClient error ', err);
      }
    };

    setStates(addressService.getStates());

    if (action === 'edit') {
      fetchClient();
    } else {
      if (me.type === 'OFFICE') {
        setBillingModalitiesPanel([
          'PAY_PER_USE',
          'BASIC_LEGACY_PLAN',
          'BASIC_PLAN',
          'ESSENTIAL_PLAN',
          'ADVANCED_PLAN',
          'PROFESSIONAL_PLAN',
        ]);
      } else {
        setBillingModalitiesPanel([
          'PAY_PER_USE',
          'BASIC_PLAN',
          'ESSENTIAL_PLAN',
          'ADVANCED_PLAN',
          'PROFESSIONAL_PLAN',
        ]);
      }
      setLoading(false);
    }
  }, [action, id]);

  const handleChange = async evt => {
    const { name } = evt.target;
    let { value } = evt.target;

    if (name === 'document' && typeof value === 'string') {
      value = value.toUpperCase();
    }

    const parsed = parseInputName(name, value);
    const parent = Object.keys(parsed)[0];

    if (name === 'client_type') setType(value);

    if (name === 'showBillingAddress') {
      if (value === 'true') {
        const state = { ...client };
        delete state.billing_address;

        setClient(state);
      }

      setShowBillingAddress(value === 'true');
      return;
    }

    if (name.includes('zipcode') && addressService.validateZipcode(value)) {
      await fillAddress(parent, value);
      return;
    }

    if (name.includes('state_id')) {
      // Update state immediately
      value = isBrazilianFranchisee ? Number(value) : value;
      parsed[parent].state_id = value;
      parsed[parent].state = states.find(state => state?.id === value)?.name;

      // Update client state immediately
      const parsedValue =
        typeof parsed[parent] !== 'string' ? { ...client[parent], ...parsed[parent] } : parsed[parent];
      setClient({ ...client, [parent]: parsedValue });

      // Load cities asynchronously without blocking the UI
      fillCities(parent, value);
      return;
    }

    // Handle city changes - allow custom values and update immediately
    if (name.includes('city') && !name.includes('city_id')) {
      // Always update the city value immediately
      parsed[parent].city = value;

      // Update client state immediately
      const parsedValue =
        typeof parsed[parent] !== 'string' ? { ...client[parent], ...parsed[parent] } : parsed[parent];
      setClient({ ...client, [parent]: parsedValue });

      // Find city_id and load neighborhoods asynchronously
      const cityList = name.includes('billing_address') ? billingCities : cities;
      const foundCity = cityList.find(city => city.name === value);

      if (foundCity) {
        // Existing city - update city_id and load neighborhoods
        setClient(prevClient => ({
          ...prevClient,
          [parent]: {
            ...prevClient[parent],
            city_id: foundCity.id,
          },
        }));
        fillNeighborhoods(parent, foundCity.id);
      } else {
        // Custom city - clear city_id and neighborhoods
        setClient(prevClient => ({
          ...prevClient,
          [parent]: {
            ...prevClient[parent],
            city_id: '',
            neighborhood: '',
            neighborhood_id: '',
          },
        }));

        if (parent === 'address') setNeighborhoods([]);
        if (parent === 'billing_address') setBillingNeighborhoods([]);
      }
      return;
    }

    // Handle neighborhood changes - allow custom values and update immediately
    if (name.includes('neighborhood') && !name.includes('neighborhood_id')) {
      // Always update the neighborhood value immediately
      parsed[parent].neighborhood = value;

      // Update client state immediately
      const parsedValue =
        typeof parsed[parent] !== 'string' ? { ...client[parent], ...parsed[parent] } : parsed[parent];
      setClient({ ...client, [parent]: parsedValue });

      // Find neighborhood_id asynchronously
      const neighborhoodList = name.includes('billing_address') ? billingNeighborhoods : neighborhoods;
      const foundNeighborhood = neighborhoodList.find(neighborhood => neighborhood.name === value);

      if (foundNeighborhood) {
        // Existing neighborhood - update neighborhood_id
        setClient(prevClient => ({
          ...prevClient,
          [parent]: {
            ...prevClient[parent],
            neighborhood_id: foundNeighborhood.id,
          },
        }));
      } else {
        // Custom neighborhood - clear neighborhood_id
        setClient(prevClient => ({
          ...prevClient,
          [parent]: {
            ...prevClient[parent],
            neighborhood_id: '',
          },
        }));
      }
      return;
    }

    const unmaskedDocument = typeof value === 'string' ? value.toUpperCase().replace(/[^A-Z0-9]+/g, '') : '';
    if (name === 'document' && isBrazilianFranchisee && unmaskedDocument.length === 14 && action === 'new') {
      findCnpj(value);
    }

    const parsedValue = typeof parsed[parent] !== 'string' ? { ...client[parent], ...parsed[parent] } : parsed[parent];
    setClient({ ...client, [parent]: parsedValue });
    setClient({ ...client, [parent]: parsedValue });
  };

  const handleSubmit = async evt => {
    evt.preventDefault();

    try {
      setBusy(true);

      // Validate documents before submitting
      let hasValidationErrors = false;

      if (client.document) {
        const isDocumentValid = validateDocumentField(client.document, type);
        if (!isDocumentValid) hasValidationErrors = true;
      }

      if (hasValidationErrors) {
        dispatch(
          showToast({
            type: 'error',
            title: intl.formatMessage({ id: 'ERROR' }),
            text: intl.formatMessage({ id: 'CLIENT_LINK.INVALID_DOCUMENT' }),
            duration: 5000,
          })
        );
        setBusy(false);
        return;
      }

      const payload = { ...client };

      // Ensure 'hotsite_configuration' exists, initialize it if undefined
      if (!payload.hotsite_configuration) {
        payload.hotsite_configuration = {}; // Initialize as an empty object
      }

      if (
        payload?.hotsite_configuration?.can_register_maintenances === undefined ||
        payload?.hotsite_configuration?.can_register_maintenances === 'true'
      ) {
        payload.hotsite_configuration.can_register_maintenances = true;
      } else if (payload?.hotsite_configuration?.can_register_maintenances === 'false') {
        payload.hotsite_configuration.can_register_maintenances = false;
      }

      if (action === 'edit') {
        await apiRequest.put(`/clients/${id}`, payload);
      } else {
        await apiRequest.post('/clients', payload);
      }

      let redirectURL;
      if (action === 'edit') {
        redirectURL = `/clients/${status}/${id}`;
      } else {
        redirectURL = '/clients';
      }

      navigate(redirectURL, { replace: true });
    } catch (err) {
      console.debug('[DEBUG] handleSubmit error ', err);

      let message;

      if (err.response) {
        const firstKey = Object.keys(err.response.data)[0];
        message = intl.formatMessage({ id: `${firstKey}.${err.response.data[firstKey]}` });
      } else {
        message = intl.formatMessage({ id: 'DATA_SAVE_ERROR' });
      }

      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: message,
          duration: 3000,
        })
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <ClientFormComponent
      action={action}
      busy={busy}
      client={client}
      clientId={id}
      loading={loading}
      loadingCnpj={loadingCnpj}
      showBillingAddress={showBillingAddress}
      classifications={classifications}
      billingModalities={billingModalities}
      billingModalitiesPanel={billingModalitiesPanel}
      zipcodeMask={zipcodeMask}
      states={states}
      cities={cities}
      billingCities={billingCities}
      neighborhoods={neighborhoods}
      billingNeighborhoods={billingNeighborhoods}
      status={status}
      type={type}
      intl={intl}
      onChange={handleChange}
      onSubmit={handleSubmit}
      cnpjMask={cnpjMask}
      cpfOnlyMask={cpfOnlyMask}
      phoneMask={phoneMask}
      cellphoneMask={cellphoneMask}
      cellphoneMaskLength={cellphoneMaskLength}
      franchiseeCountry={franchiseeCountry}
      documentError={documentError}
      useTextInputForAddress={useTextInputForAddress}
    />
  );
};

ClientFormContainer.propTypes = {
  action: PropTypes.string.isRequired,
};

export default ClientFormContainer;
